import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Icon, Label, Table } from 'semantic-ui-react';
import { API, showError, timestamp2string } from '../../helpers';
import '../TopUp/TopUp.css';

const statusMap = {
  active: { text: '可用', color: 'green' },
  exhausted: { text: '已用尽', color: 'orange' },
  expired: { text: '已过期', color: 'grey' },
  refunded: { text: '已退款', color: 'violet' },
};

const usageTypeMap = {
  consume: { text: '消耗', color: 'red' },
  refund: { text: '退回', color: 'green' },
};

function formatAmount(value) {
  return `¥${Number(value || 0).toFixed(2)}`;
}

function formatTokenQuota(value) {
  const quota = Number(value || 0);
  if (quota >= 100000000) return `${(quota / 100000000).toFixed(2)} 亿`;
  if (quota >= 10000) return `${(quota / 10000).toFixed(2)} 万`;
  return String(quota);
}

function getStatus(subscription) {
  if (
    subscription?.status === 'active' &&
    subscription?.expired_time > 0 &&
    subscription.expired_time * 1000 <= Date.now()
  ) {
    return statusMap.expired;
  }
  return statusMap[subscription?.status] || {
    text: subscription?.status || '-',
    color: 'grey',
  };
}

function formatUsageDelta(record) {
  const delta = Math.abs(Number(record?.quota_delta || 0));
  const sign = record?.event_type === 'refund' ? '+' : '-';
  return `${sign}${formatTokenQuota(delta)} Tokens`;
}

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [usageRecords, setUsageRecords] = useState([]);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);

  const selectedSubscription = useMemo(
    () =>
      subscriptions.find(
        (subscription) => subscription.id === selectedSubscriptionId
      ),
    [subscriptions, selectedSubscriptionId]
  );

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/topup/subscriptions');
      const { success, message, data } = res.data || {};
      if (!success) {
        showError(message);
        return [];
      }
      const list = data || [];
      setSubscriptions(list);
      setSelectedSubscriptionId((currentId) => {
        if (currentId && list.some((item) => item.id === currentId)) {
          return currentId;
        }
        return list[0]?.id || 0;
      });
      return list;
    } finally {
      setLoading(false);
    }
  };

  const loadUsageRecords = async (subscriptionId = selectedSubscriptionId) => {
    const id = Number(subscriptionId || 0);
    if (!id) {
      setUsageRecords([]);
      return;
    }
    setUsageLoading(true);
    try {
      const res = await API.get(
        `/api/topup/subscription-usage?p=0&subscription_id=${id}`
      );
      const { success, message, data } = res.data || {};
      if (success) {
        setUsageRecords(data || []);
      } else {
        showError(message);
      }
    } finally {
      setUsageLoading(false);
    }
  };

  const refresh = async () => {
    const list = await loadSubscriptions();
    const nextId =
      selectedSubscriptionId &&
      list.some((item) => item.id === selectedSubscriptionId)
        ? selectedSubscriptionId
        : list[0]?.id || 0;
    await loadUsageRecords(nextId);
  };

  useEffect(() => {
    loadSubscriptions().catch(showError);
  }, []);

  useEffect(() => {
    if (selectedSubscriptionId) {
      loadUsageRecords(selectedSubscriptionId).catch(showError);
    } else {
      setUsageRecords([]);
    }
  }, [selectedSubscriptionId]);

  return (
    <div className='dashboard-container'>
      <div className='topup-shell'>
        <Card fluid className='topup-order-card topup-resource-card'>
          <Card.Content>
            <div className='topup-panel-head'>
              <div>
                <h3>已订购资源包</h3>
                <p>这里展示当前账户已购买的资源包、有效期、剩余额度和扣量明细。</p>
              </div>
              <Button
                basic
                icon
                labelPosition='left'
                loading={loading}
                onClick={refresh}
              >
                <Icon name='refresh' />
                刷新
              </Button>
            </div>
            <Table basic='very' className='topup-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>资源包</Table.HeaderCell>
                  <Table.HeaderCell>总量</Table.HeaderCell>
                  <Table.HeaderCell>已用 / 剩余</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                  <Table.HeaderCell>订购时间</Table.HeaderCell>
                  <Table.HeaderCell>到期时间</Table.HeaderCell>
                  <Table.HeaderCell>明细</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {subscriptions.map((subscription) => {
                  const status = getStatus(subscription);
                  return (
                    <Table.Row key={subscription.id}>
                      <Table.Cell>
                        <strong>{subscription.plan_name}</strong>
                        <span className='topup-muted'>{subscription.model_name}</span>
                      </Table.Cell>
                      <Table.Cell>
                        {formatTokenQuota(subscription.quota)} Tokens
                      </Table.Cell>
                      <Table.Cell>
                        {formatTokenQuota(subscription.used_quota)} /{' '}
                        {formatTokenQuota(subscription.remaining_quota)} Tokens
                      </Table.Cell>
                      <Table.Cell>
                        <Label color={status.color}>{status.text}</Label>
                      </Table.Cell>
                      <Table.Cell>{timestamp2string(subscription.started_time)}</Table.Cell>
                      <Table.Cell>{timestamp2string(subscription.expired_time)}</Table.Cell>
                      <Table.Cell>
                        <Button
                          size='mini'
                          primary={selectedSubscriptionId === subscription.id}
                          basic={selectedSubscriptionId !== subscription.id}
                          onClick={() => setSelectedSubscriptionId(subscription.id)}
                        >
                          查看
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
                {subscriptions.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='7' textAlign='center'>
                      暂无已订购资源包
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Card.Content>
        </Card>

        {selectedSubscription && (
          <Card fluid className='topup-order-card topup-resource-card'>
            <Card.Content>
              <div className='topup-panel-head'>
                <div>
                  <h3>{selectedSubscription.plan_name} 用量明细</h3>
                  <p>
                    剩余 {formatTokenQuota(selectedSubscription.remaining_quota)} Tokens，到期{' '}
                    {timestamp2string(selectedSubscription.expired_time)}
                  </p>
                </div>
                <Button
                  basic
                  icon
                  labelPosition='left'
                  loading={usageLoading}
                  onClick={() => loadUsageRecords(selectedSubscriptionId)}
                >
                  <Icon name='refresh' />
                  刷新明细
                </Button>
              </div>
              <Table basic='very' className='topup-table'>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>时间</Table.HeaderCell>
                    <Table.HeaderCell>类型</Table.HeaderCell>
                    <Table.HeaderCell>令牌</Table.HeaderCell>
                    <Table.HeaderCell>Tokens 变动</Table.HeaderCell>
                    <Table.HeaderCell>剩余</Table.HeaderCell>
                    <Table.HeaderCell>金额</Table.HeaderCell>
                    <Table.HeaderCell>来源 / 备注</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {usageRecords.map((record) => {
                    const usageType = usageTypeMap[record.event_type] || {
                      text: record.event_type || '-',
                      color: 'grey',
                    };
                    const amount = Number(record.amount_rmb || 0);
                    return (
                      <Table.Row key={record.id}>
                        <Table.Cell>{timestamp2string(record.created_time)}</Table.Cell>
                        <Table.Cell>
                          <Label color={usageType.color}>{usageType.text}</Label>
                        </Table.Cell>
                        <Table.Cell>
                          {record.token_name || '-'}
                          {record.token_id ? (
                            <span className='topup-muted'>#{record.token_id}</span>
                          ) : null}
                        </Table.Cell>
                        <Table.Cell>
                          <Label color={usageType.color}>
                            {formatUsageDelta(record)}
                          </Label>
                        </Table.Cell>
                        <Table.Cell>
                          {formatTokenQuota(record.remaining_after)} Tokens
                        </Table.Cell>
                        <Table.Cell>
                          {amount === 0
                            ? '-'
                            : `${amount > 0 ? '+' : '-'}${formatAmount(Math.abs(amount))}`}
                        </Table.Cell>
                        <Table.Cell>
                          {record.source_no || record.source_type || '-'}
                          {record.remark ? (
                            <span className='topup-muted'>{record.remark}</span>
                          ) : null}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                  {usageRecords.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan='7' textAlign='center'>
                        暂无用量明细
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </Card.Content>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;
